import { useEffect, useState, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin, Upload } from 'antd'
import { Upload as UploadIcon, Plus, XCircle } from 'lucide-react'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { Unit, UIConfigPayload } from '@/types/api'
import { uploadMultipartFile } from '@/api/file.api'
import {
  getUnitUIConfig,
  updateUnitUIConfig,
  getUnitBackgrounds,
  uploadUnitBackground,
  deleteUnitBackground,
} from '@/api/units.api'

interface UnitUIConfigDrawerProps {
  unit: Unit | null
  onClose: () => void
}

const PILL_INPUT =
  '!bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

const TEXT_COLORS = [
  {
    value: '#FBBF24',
    bgClass: 'bg-[#FBBF24]',
    borderClass: 'border-transparent',
    ringClass: 'ring-[#FBBF24]',
  },
  {
    value: '#FFFFFF',
    bgClass: 'bg-[#FFFFFF]',
    borderClass: 'border-black',
    ringClass: 'ring-black',
  },
  {
    value: '#0F172A',
    bgClass: 'bg-[#0F172A]',
    borderClass: 'border-transparent',
    ringClass: 'ring-[#0F172A]',
  },
]

export function UnitInterfaceConfigDrawer({
  unit,
  onClose,
}: UnitUIConfigDrawerProps) {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const isOpen = !!unit

  const selectedTextColor = Form.useWatch('textColor', form)
  const selectedBackgroundId = Form.useWatch('backgroundId', form)

  const [logoPreview, setLogoPreview] = useState<{
    id: string
    url: string
    name: string
  } | null>(null)

  const pendingUploads = useRef<
    { file: File; onSuccess?: () => void; onError?: (err: Error) => void }[]
  >([])
  const bgUploadTimeout = useRef<NodeJS.Timeout | null>(null)

  const { data: backgrounds = [], isLoading: isFetchingBgs } = useQuery({
    queryKey: ['unit-backgrounds'],
    queryFn: ({ signal }) => getUnitBackgrounds(signal),
    enabled: isOpen,
  })

  const { data: configDetail, isLoading: isFetchingConfig } = useQuery({
    queryKey: ['unit-ui-config', unit?.id],
    queryFn: ({ signal }) =>
      unit?.id ? getUnitUIConfig(unit.id, signal) : Promise.resolve(null),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen && configDetail) {
      const rawColor = configDetail.textColor || 'FFFFFFFF'
      const mappedColor = `#${rawColor.slice(-6).toUpperCase()}`

      form.setFieldsValue({
        mainTitle: configDetail.mainTitle || '',
        subTitle: configDetail.subTitle || '',
        textColor: mappedColor,
        backgroundId: configDetail.backgroundFiles?.id || null,
        logoId: configDetail.logoFiles?.id || null,
      })

      if (configDetail.logoFiles) {
        setLogoPreview({
          id: configDetail.logoFiles.id,
          url: configDetail.logoFiles.fileUrl,
          name: configDetail.logoFiles.originFileName,
        })
      }
    } else if (!isOpen) {
      form.resetFields()
      setLogoPreview(null)
    }
  }, [isOpen, configDetail, form])

  const saveMutation = useMutation({
    mutationFn: (values: UIConfigPayload) => {
      if (!unit?.id) throw new Error('Không tìm thấy mã đơn vị')
      return updateUnitUIConfig(unit.id, values)
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật cấu hình giao diện thành công!'
      notification.success({ title: successMessage })

      queryClient.invalidateQueries({ queryKey: ['unit-ui-config', unit?.id] })
      onClose()
    },
  })

  const deleteBgMutation = useMutation({
    mutationFn: (id: string) => deleteUnitBackground(id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Xóa ảnh nền thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['unit-backgrounds'] })
    },
  })

  const customLogoUpload = async ({ file }: any) => {
    const logoId = await uploadMultipartFile(file, 'unit-logo')
    setLogoPreview({
      id: logoId,
      url: URL.createObjectURL(file),
      name: file.name,
    })
    form.setFieldsValue({ logoId })
  }

  const customBgUpload = ({ file, onSuccess, onError }: any) => {
    pendingUploads.current.push({ file, onSuccess, onError })

    if (bgUploadTimeout.current) clearTimeout(bgUploadTimeout.current)

    bgUploadTimeout.current = setTimeout(async () => {
      const uploads = [...pendingUploads.current]
      pendingUploads.current = []

      try {
        const filesToUpload = uploads.map((u) => u.file)
        const response = await uploadUnitBackground(filesToUpload)
        const successMessage =
          response.responseMessage || 'Tải lên ảnh nền thành công!'
        notification.success({ message: successMessage })
        uploads.forEach((u) => u.onSuccess?.())
      } catch (error: any) {
        uploads.forEach((u) => u.onError?.(error))
      } finally {
        queryClient.invalidateQueries({ queryKey: ['unit-backgrounds'] })
      }
    }, 100)
  }

  const handleSubmit = (values: any) => {
    const submitTextColor = values.textColor.replace('#', 'FF')
    const payload: UIConfigPayload = {
      logoId: values.logoId || logoPreview?.id,
      mainTitle: values.mainTitle,
      subTitle: values.subTitle,
      textColor: submitTextColor,
      backgroundId: values.backgroundId,
    }

    saveMutation.mutate(payload)
  }

  const isSubmitting = saveMutation.isPending
  const isDrawerDisabled = isSubmitting || isFetchingConfig || isFetchingBgs

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      destroyOnClose
      title="CẤU HÌNH GIAO DIỆN"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[120px]"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[120px]"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin
        spinning={isFetchingConfig || isFetchingBgs}
        description="Đang tải dữ liệu..."
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ textColor: '#FFFFFF' }}
          requiredMark={(label, info) => (
            <>
              {label}
              {info.required && <span className="text-red-500 ml-1">*</span>}
            </>
          )}
          className="space-y-6 min-h-[300px] mt-2"
        >
          <Form.Item
            name="logoId"
            hidden
            rules={[{ required: true, message: 'Vui lòng tải lên logo' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="backgroundId"
            hidden
            rules={[{ required: true, message: 'Vui lòng chọn hình nền' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="textColor" hidden>
            <Input />
          </Form.Item>

          <div className="rounded-xl border border-solid border-gray-300 px-5 pt-4 pb-2">
            <h3 className="text-lg font-semibold mb-4">Tiêu đề</h3>
            <Form.Item label="Logo" required>
              <Upload
                customRequest={customLogoUpload}
                maxCount={1}
                showUploadList={false}
                accept=".jpg,.jpeg,.png,.svg,.webp"
                className="[&_.ant-upload-select]:!w-full block"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className={`w-full flex items-center justify-between border-gray-300 overflow-hidden ${PILL_INPUT}`}
                >
                  <div className="flex items-center truncate">
                    {logoPreview ? (
                      <span className="truncate">{logoPreview.name}</span>
                    ) : (
                      <span className="text-[#bfbfbf] truncate">
                        Tải lên logo
                      </span>
                    )}
                  </div>
                  <UploadIcon size={16} />
                </Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="mainTitle"
              label="Tiêu đề chính"
              rules={[
                { required: true, message: 'Vui lòng nhập tiêu đề chính' },
              ]}
            >
              <Input
                placeholder="Nhập tiêu đề chính"
                size="large"
                className={PILL_INPUT}
              />
            </Form.Item>

            <Form.Item
              name="subTitle"
              label="Tiêu đề phụ"
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề phụ' }]}
            >
              <Input
                placeholder="Nhập tiêu đề phụ"
                size="large"
                className={PILL_INPUT}
              />
            </Form.Item>
          </div>

          <div className="rounded-xl border border-solid border-gray-300 p-5">
            <div className="mb-4 flex flex-col text-left">
              <span className="text-lg font-semibold">Hiển thị</span>
              <span className="text-sm font-normal text-gray-500">
                Tùy chọn hiển thị trên robot
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span>Màu chữ</span>
              <div className="flex items-center justify-end gap-3">
                {TEXT_COLORS.map((color) => {
                  const isSelected = selectedTextColor === color.value
                  return (
                    <Button
                      key={color.value}
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        form.setFieldsValue({ textColor: color.value })
                      }
                      className={`
                                                    !h-6 !w-6 !min-w-0 !p-0 transition-all rounded-md border border-solid
                                                    ${color.bgClass} 
                                                    ${color.borderClass}
                                                    ${
                                                      !isSelected
                                                        ? 'hover:scale-110'
                                                        : `ring-2 ring-offset-[3px] ${color.ringClass}`
                                                    }
                    `}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-solid border-gray-300 p-5">
            <div className="mb-4 flex flex-col text-left">
              <span className="text-lg font-semibold">Hình nền</span>
              <span className="text-sm font-normal text-gray-500">
                Tùy chọn hình nền hiển thị trên robot
              </span>
            </div>

            <Upload
              listType="picture-card"
              fileList={backgrounds.map((bg: any) => ({
                uid: bg.id,
                name: bg.originFileName,
                status: 'done',
                url: bg.fileUrl,
              }))}
              multiple
              customRequest={customBgUpload}
              accept=".jpg,.jpeg,.png,.svg,.webp"
              className="
                                [&_.ant-upload-list]:!flex [&_.ant-upload-list]:!flex-wrap
                                [&_.ant-upload-list-item-container]:!w-[169px] [&_.ant-upload-list-item-container]:!h-[100px]
                                [&_.ant-upload-select]:!w-[100px] [&_.ant-upload-select]:!h-[100px]
                            "
              itemRender={(_, file) => {
                const isSelected = selectedBackgroundId === file.uid
                const isUploading = file.status === 'uploading'

                if (isUploading) {
                  return (
                    <div className="flex h-[100px] w-[169px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                      <Spin />
                    </div>
                  )
                }

                return (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    aria-label={`Chọn hình nền ${file.name}`}
                    onClick={() =>
                      form.setFieldsValue({ backgroundId: file.uid })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        form.setFieldsValue({ backgroundId: file.uid })
                      }
                    }}
                    className="group relative h-[100px] w-[169px] cursor-pointer overflow-hidden rounded-xl"
                  >
                    <img
                      src={file.url}
                      alt={file.name}
                      className="pointer-events-none h-full w-full object-cover"
                    />
                    <div
                      className={`absolute inset-0 rounded-xl border-solid ${
                        isSelected
                          ? 'border-[4px] border-[#CE7A58]'
                          : 'border-[1px] border-gray-300'
                      }`}
                    />

                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-6 w-6 !min-w-0 !bg-transparent !p-0 disabled:cursor-not-allowed disabled:opacity-30"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (configDetail?.backgroundFiles?.id === file.uid)
                          return
                        deleteBgMutation.mutate(file.uid)
                      }}
                      disabled={
                        deleteBgMutation.isPending ||
                        configDetail?.backgroundFiles?.id === file.uid
                      }
                    >
                      <XCircle
                        size={18}
                        className="shrink-0 text-white/80 drop-shadow-md transition-colors hover:text-white"
                      />
                    </Button>
                  </div>
                )
              }}
            >
              <Button
                variant="outline"
                className="flex h-[100px] w-[100px] justify-center !rounded-xl border-[2px] border-[#CE7A58] text-[#CE7A58] hover:!border-[2px] hover:bg-[#CE7A58]/10"
              >
                <Plus size={40} />
              </Button>
            </Upload>
          </div>
        </Form>
      </Spin>
    </Drawer>
  )
}
